import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import throttle from "lodash/throttle";

import OrderDetails from "../../components/OrderDetails/OrderDetails";
import { useOrder } from "../../store/backend/order";
import { usePunchPayment } from "../../store/backend/usePunchPayment";
import { formatErrorDetailsByResponseStatusCode } from "../../helpers/errorFormatter";
import Loader from "../../components/Loader/Loader";
import { RazorpayPaymentHandler } from "../PaymentGateway/razorpayPaymentGateway";
import { PayUPaymentHandler } from "../PaymentGateway/payuPaymentGateway";

import { ERROR_MESSAGES, PROVIDERS } from "../../constants/constants";
import { useHeaderContext } from "../../context/HeaderContext";
import ErrorMessage from "../../widgets/ErrorMessage/ErrorMessage";

import styles from "./OrderPaymentContainer.module.scss";

const handleOnConfirm = throttle(
  (createPaymentOrder, orderId, orderAmount, provider_id) => {
    createPaymentOrder({
      order_id: orderId,
      amount: orderAmount,
      provider_id: provider_id,
    });
  },
  5000
);

const paymentHandler = (order, punchPaymentResponse) => {
  if (order.provider_id === PROVIDERS.RAZORPAY) {
    RazorpayPaymentHandler(punchPaymentResponse, order);
    return;
  }

  if (order.provider_id === PROVIDERS.PAYU) {
    PayUPaymentHandler(
      punchPaymentResponse.payment_mode_details,
      order.payment_amount
    );
    return;
  }
};

const OrderPaymentsContainer = () => {
  const { orderId } = useParams();
  const { setVerticalId } = useHeaderContext();

  const {
    isLoading: isLoadingOrder,
    data: order,
    error: orderError,
  } = useOrder(orderId);

  useEffect(() => {
    if (order?.vertical_id) {
      setVerticalId(order.vertical_id);
    }
  }, [order?.vertical_id, setVerticalId]);

  const {
    isPending: isCreatingPaymentOrder,
    isSuccess: isPunchPaymentSuccess,
    mutate: createPaymentOrder,
    error: paymentError,
  } = usePunchPayment({
    onSuccess: (punchPaymentResponse) => {
      paymentHandler(order, punchPaymentResponse);
    },
  });

  if (isLoadingOrder) {
    return <Loader />;
  }

  if (orderError) {
    return (
      <div className={styles.errorContainer}>
        <ErrorMessage>
          {formatErrorDetailsByResponseStatusCode(orderError)}
        </ErrorMessage>
      </div>
    );
  }

  if (paymentError) {
    return (
      <div className={styles.errorContainer}>
        <ErrorMessage>
          {paymentError.message || ERROR_MESSAGES.PROCESSING_ERROR}
        </ErrorMessage>
      </div>
    );
  }

  if (isPunchPaymentSuccess) {
    return <Loader />;
  }

  return (
    <div className={styles.container}>
      <div className={styles.contentContainer}>
        <OrderDetails
          order={order}
          onConfirmPayment={() =>
            handleOnConfirm(
              createPaymentOrder,
              orderId,
              order.payment_amount,
              order.provider_id
            )
          }
          isCreatingPaymentOrder={isCreatingPaymentOrder}
          isPunchPaymentSuccess={isPunchPaymentSuccess}
        />
      </div>
    </div>
  );
};

export default OrderPaymentsContainer;
